import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ordersAPI } from '../api/vendly';
import styles from './OrderTrackingPage.module.css';

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;

const STEPS = [
  { key: 'placed',           label: 'Order placed',         icon: '🛒', desc: 'Your order has been received'         },
  { key: 'pending_payment',  label: 'Awaiting payment',     icon: '💳', desc: 'Transfer payment to complete order'   },
  { key: 'paid',             label: 'Payment confirmed',    icon: '💸', desc: 'Admin has verified your payment'      },
  { key: 'shipped',          label: 'Waiting to ship',      icon: '📦', desc: 'Your items are being prepared'        },
  { key: 'delivered',        label: 'Delivered',            icon: '🎉', desc: 'Your order has been delivered'        },
];

// Map actual order status to step index
const STATUS_STEP = {
  pending:         1,
  pending_payment: 2,
  paid:            3,
  shipped:         4,
  delivered:       5,
  cancelled:      -1,
};

const STATUS_COLOR = {
  pending:         { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)',  label: 'Pending payment'     },
  pending_payment: { color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', label: 'Payment verification' },
  paid:            { color: '#10B981', bg: 'rgba(16,185,129,0.1)', label: 'Paid'                 },
  shipped:         { color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)', label: 'Waiting to ship'      },
  delivered:       { color: '#10B981', bg: 'rgba(16,185,129,0.1)', label: 'Delivered'            },
  cancelled:       { color: '#EF4444', bg: 'rgba(239,68,68,0.1)',  label: 'Cancelled'            },
};

export default function OrderTrackingPage() {
  const { id }    = useParams();
  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    ordersAPI.get(id)
      .then(res => setOrder(res.data.order))
      .catch(() => setError('Order not found'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className={styles.page}>
      <div className="container">
        <div className={styles.loadingWrap}><div className={styles.spin} /></div>
      </div>
    </div>
  );

  if (error || !order) return (
    <div className={styles.page}>
      <div className="container">
        <div className={styles.errorState}>
          <span>❌</span>
          <h2>Order not found</h2>
          <Link to="/orders" className={styles.backBtn}>← My orders</Link>
        </div>
      </div>
    </div>
  );

  const currentStep = STATUS_STEP[order.status] ?? 1;
  const cancelled   = order.status === 'cancelled';
  const st          = STATUS_COLOR[order.status] || STATUS_COLOR.pending;

  return (
    <div className={styles.page}>
      <div className="container">

        {/* Header */}
        <div className={styles.header}>
          <Link to="/orders" className={styles.backLink}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            My orders
          </Link>
          <div className={styles.orderMeta}>
            <h1 className={styles.orderTitle}>Order #{order.id}</h1>
            <p className={styles.orderDate}>
              Placed {new Date(order.createdAt).toLocaleDateString('en-NG', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
            </p>
          </div>
          <span className={styles.statusBadge} style={{ color: st.color, background: st.bg }}>
            {st.label}
          </span>
        </div>

        <div className={styles.layout}>
          {/* Left — tracking timeline */}
          <div className={styles.main}>

            {/* Timeline */}
            <div className={styles.timelineCard}>
              <h2 className={styles.cardTitle}>Order tracking</h2>

              {cancelled ? (
                <div className={styles.cancelled}>
                  <span>❌</span>
                  <p>This order was cancelled</p>
                </div>
              ) : (
                <div className={styles.timeline}>
                  {STEPS.map((step, i) => {
                    const stepNum   = i + 1;
                    const isDone    = stepNum < currentStep;
                    const isCurrent = stepNum === currentStep;
                    const isPending = stepNum > currentStep;

                    return (
                      <div key={step.key} className={`${styles.step} ${isDone ? styles.stepDone : isCurrent ? styles.stepCurrent : styles.stepPending}`}>
                        <div className={styles.stepLeft}>
                          <div className={styles.stepDot}>
                            {isDone ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            ) : isCurrent ? (
                              <div className={styles.stepPulse} />
                            ) : (
                              <span className={styles.stepNum}>{stepNum}</span>
                            )}
                          </div>
                          {i < STEPS.length - 1 && (
                            <div className={`${styles.stepLine} ${isDone ? styles.stepLineDone : ''}`} />
                          )}
                        </div>
                        <div className={styles.stepRight}>
                          <div className={styles.stepHeader}>
                            <span className={styles.stepIcon}>{step.icon}</span>
                            <p className={styles.stepLabel}>{step.label}</p>
                          </div>
                          <p className={styles.stepDesc}>{isCurrent ? <strong>{step.desc}</strong> : step.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Items */}
            <div className={styles.itemsCard}>
              <h2 className={styles.cardTitle}>Items ordered</h2>
              <div className={styles.itemsList}>
                {order.items?.map((item, i) => (
                  <div key={i} className={styles.item}>
                    {item.image
                      ? <img src={item.image} alt={item.name} className={styles.itemImg} />
                      : <div className={styles.itemImgPlaceholder}>📦</div>
                    }
                    <div className={styles.itemInfo}>
                      <p className={styles.itemName}>{item.name || item.productName || `Product #${item.productId}`}</p>
                      <p className={styles.itemMeta}>Qty: {item.quantity} × {fmt(item.unitPrice)}</p>
                    </div>
                    <p className={styles.itemTotal}>{fmt(item.unitPrice * item.quantity)}</p>
                  </div>
                ))}
              </div>
              <div className={styles.totals}>
                <div className={styles.totalRow}><span>Subtotal</span><span>{fmt(order.total - order.deliveryFee)}</span></div>
                <div className={styles.totalRow}><span>Delivery</span><span>{fmt(order.deliveryFee)}</span></div>
                <div className={`${styles.totalRow} ${styles.totalGrand}`}><span>Total</span><span>{fmt(order.total)}</span></div>
              </div>
            </div>
          </div>

          {/* Right — delivery & payment info */}
          <div className={styles.sidebar}>

            {/* Delivery address */}
            <div className={styles.infoCard}>
              <h3 className={styles.infoTitle}>📍 Delivery address</h3>
              <p className={styles.infoName}>{order.address?.name}</p>
              <p className={styles.infoText}>{order.address?.line}</p>
              <p className={styles.infoText}>{order.address?.city}, {order.address?.state}</p>
              <p className={styles.infoText}>📞 {order.address?.phone}</p>
            </div>

            {/* Payment method */}
            <div className={styles.infoCard}>
              <h3 className={styles.infoTitle}>💳 Payment</h3>
              <p className={styles.infoText}>{order.paymentMethod === 'transfer' ? 'Bank transfer' : order.paymentMethod}</p>
              <p className={styles.infoText} style={{ color: st.color }}>{st.label}</p>
            </div>

            {/* Need help */}
            <div className={styles.helpCard}>
              <h3 className={styles.infoTitle}>Need help?</h3>
              <p className={styles.helpText}>Use your order number <strong>#{order.id}</strong> when contacting support.</p>
              <Link to="/support" className={styles.helpBtn}>Contact support</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
